/**
 * login_v2.js — DAS-108 Phase 06: Login V2 · IS 리프레시 시안 매칭
 *
 * 매핑: LoginDesktopV1 + LoginMobileV1 → 중앙 정렬 미니멀 카드
 *
 * 인증 로직: pages/login.js 의 LoginPageV1(LoginPage) 과 동일
 *   - loginUser (balanceApi), setLoginSession / consumeSessionExpired (auth)
 *   - state: username / password / remember / error / isSubmitting / showPassword
 *
 * 디자인 시스템 토큰 전용. raw hex 금지. arbitrary Tailwind 금지 (min-h-[44px] 예외).
 * primitives: Btn, Field, Input, HexLogo, V2Icon 적극 활용 (DAS-102).
 * a11y: form input 필수 label/aria, password 토글 aria-label 필수.
 * resize 리스너 → isMobileViewport() 로 mobile/desktop 분기.
 */

import React from 'react';
import { loginUser } from '../../services/balanceApi';
import { consumeSessionExpired, setLoginSession, getStoredUsername } from '../../utils/auth';
import { isMobileViewport } from '../../utils/responsive';
import { HexLogo, V2Icon, Btn, Input } from '../common/claude_primitives';

/* ── 배경 격자 ───────────────────────────────────────────── */
function BgGrid() {
    return (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at 50% 30%, color-mix(in oklch, var(--color-brand) 6%, transparent), transparent 55%)',
            }} />
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.3 }}>
                <defs>
                    <pattern id="v2-login-v2-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--color-surface-border)" strokeWidth="0.5" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#v2-login-v2-grid)" />
            </svg>
        </div>
    );
}

/* ── 인라인 에러 ─────────────────────────────────────────── */
function InlineError({ message }) {
    if (!message) return null;
    return (
        <div
            role="alert"
            className="rounded-lg border border-loss/20 bg-loss-subtle px-3 py-2.5 text-sm text-loss"
        >
            {message}
        </div>
    );
}

/* ── 커스텀 체크박스 ──────────────────────────────────────── */
function Checkbox({ checked, onClick, label, id }) {
    return (
        <label
            htmlFor={id}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 44, paddingTop: 4, paddingBottom: 4 }}
        >
            <button
                id={id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={onClick}
                style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: checked ? '1px solid var(--color-brand)' : '1px solid var(--color-surface-border-light)',
                    background: checked ? 'var(--color-brand)' : 'var(--color-surface-elevated)',
                    display: 'grid', placeItems: 'center', flexShrink: 0, cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s',
                }}
            >
                {checked && <V2Icon name="check" size={10} style={{ color: 'var(--color-content-inverse)' }} />}
            </button>
            <span style={{ fontSize: 12, color: 'var(--color-content-secondary)' }}>{label}</span>
        </label>
    );
}

/* ── 데스크톱 V1 (IS 리프레시): 중앙 정렬 미니멀 카드 ─────── */
function LoginDesktopIsRefresh({ state, handlers }) {
    const { username, password, remember, error, isSubmitting, showPassword } = state;
    const { onUsernameChange, onPasswordChange, onRememberToggle, onShowPasswordToggle, onSubmit } = handlers;

    return (
        <div style={{
            minHeight: '100vh',
            position: 'relative',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--color-surface)',
        }}>
            <BgGrid />

            <div style={{
                width: '100%', maxWidth: 380,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 22,
            }}>
                {/* 로고 + 브랜드 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        border: '1.5px solid var(--color-content)',
                        display: 'grid', placeItems: 'center',
                    }}>
                        <HexLogo size={26} stroke="var(--color-content)" accent="var(--color-brand)" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: 20, fontWeight: 600, letterSpacing: '-0.015em',
                            color: 'var(--color-content)',
                        }}>
                            MyDashboard
                        </div>
                        <div style={{
                            fontSize: 12, marginTop: 4, letterSpacing: '0.1em',
                            color: 'var(--color-content-muted)',
                            fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            v2 · INTERNAL
                        </div>
                    </div>
                </div>

                {/* 로그인 카드 */}
                <form
                    aria-label="로그인 폼"
                    onSubmit={onSubmit}
                    style={{
                        width: '100%',
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 16,
                        padding: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14,
                        boxShadow: 'var(--shadow-card)',
                    }}
                >
                    <InlineError message={error} />

                    {/* USERNAME */}
                    <div>
                        <label
                            htmlFor="v2d-username"
                            style={{
                                display: 'block',
                                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', marginBottom: 6, fontWeight: 500,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}
                        >
                            Username
                        </label>
                        <Input
                            id="v2d-username"
                            type="text"
                            value={username}
                            onChange={onUsernameChange}
                            placeholder="사용자명을 입력하세요"
                            autoComplete="username"
                        />
                    </div>

                    {/* PASSWORD */}
                    <div>
                        <label
                            htmlFor="v2d-password"
                            style={{
                                display: 'block',
                                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', marginBottom: 6, fontWeight: 500,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}
                        >
                            Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <Input
                                id="v2d-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={onPasswordChange}
                                placeholder="비밀번호를 입력하세요"
                                autoComplete="current-password"
                                style={{ paddingRight: '2.5rem' }}
                            />
                            <button
                                type="button"
                                onClick={onShowPasswordToggle}
                                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                aria-pressed={showPassword}
                                style={{
                                    position: 'absolute', right: 10, top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                    color: 'var(--color-content-muted)',
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <V2Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                            </button>
                        </div>
                    </div>

                    {/* 아이디 기억하기 */}
                    <Checkbox
                        id="v2d-remember"
                        checked={remember}
                        onClick={onRememberToggle}
                        label="아이디 기억하기"
                    />

                    {/* 제출 버튼 */}
                    <Btn
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isSubmitting}
                        style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-content-inverse/30 border-t-content-inverse" />
                                로그인 중...
                            </>
                        ) : (
                            '로그인'
                        )}
                    </Btn>
                </form>

                {/* 푸터 */}
                <div style={{
                    fontSize: 12, letterSpacing: '0.1em',
                    color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                }}>
                    © 2026 MyDashboard · Internal Use Only
                </div>
            </div>
        </div>
    );
}

/* ── 모바일 V1 (IS 리프레시): 전체폭 미니멀 카드 ─────────── */
function LoginMobileIsRefresh({ state, handlers }) {
    const { username, password, remember, error, isSubmitting, showPassword } = state;
    const { onUsernameChange, onPasswordChange, onRememberToggle, onShowPasswordToggle, onSubmit } = handlers;

    return (
        <div style={{
            minHeight: '100vh',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 22px',
            background: 'var(--color-surface)',
        }}>
            <BgGrid />

            <div style={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 20,
            }}>
                {/* 로고 + 브랜드 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 11,
                        border: '1.5px solid var(--color-content)',
                        display: 'grid', placeItems: 'center',
                    }}>
                        <HexLogo size={24} stroke="var(--color-content)" accent="var(--color-brand)" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: 18, fontWeight: 600,
                            color: 'var(--color-content)',
                        }}>
                            MyDashboard
                        </div>
                        <div style={{
                            fontSize: 12, marginTop: 4, letterSpacing: '0.1em',
                            color: 'var(--color-content-muted)',
                            fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            v2 · INTERNAL
                        </div>
                    </div>
                </div>

                {/* 로그인 카드 */}
                <form
                    aria-label="로그인 폼"
                    onSubmit={onSubmit}
                    style={{
                        width: '100%',
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 14,
                        padding: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        boxShadow: 'var(--shadow-card)',
                    }}
                >
                    <InlineError message={error} />

                    {/* USERNAME */}
                    <div>
                        <label
                            htmlFor="v2m-username"
                            style={{
                                display: 'block',
                                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', marginBottom: 6, fontWeight: 500,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}
                        >
                            Username
                        </label>
                        <Input
                            id="v2m-username"
                            type="text"
                            value={username}
                            onChange={onUsernameChange}
                            placeholder="사용자명"
                            autoComplete="username"
                        />
                    </div>

                    {/* PASSWORD */}
                    <div>
                        <label
                            htmlFor="v2m-password"
                            style={{
                                display: 'block',
                                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', marginBottom: 6, fontWeight: 500,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}
                        >
                            Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <Input
                                id="v2m-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={onPasswordChange}
                                placeholder="비밀번호"
                                autoComplete="current-password"
                                style={{ paddingRight: '2.5rem' }}
                            />
                            <button
                                type="button"
                                onClick={onShowPasswordToggle}
                                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                aria-pressed={showPassword}
                                style={{
                                    position: 'absolute', right: 10, top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                    color: 'var(--color-content-muted)',
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <V2Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                            </button>
                        </div>
                    </div>

                    {/* 아이디 기억하기 */}
                    <Checkbox
                        id="v2m-remember"
                        checked={remember}
                        onClick={onRememberToggle}
                        label="아이디 기억하기"
                    />

                    {/* 제출 버튼 */}
                    <Btn
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isSubmitting}
                        style={{ width: '100%', justifyContent: 'center', minHeight: 48 }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-content-inverse/30 border-t-content-inverse" />
                                로그인 중...
                            </>
                        ) : (
                            '로그인'
                        )}
                    </Btn>
                </form>
            </div>
        </div>
    );
}

/* ── 메인 export: LoginPageV2 ───────────────────────────── */
class LoginPageV2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            username: '',
            password: '',
            remember: true,
            error: '',
            isSubmitting: false,
            showPassword: false,
            isMobile: isMobileViewport(),
        };
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        const storedUsername = getStoredUsername();
        const sessionExpired = consumeSessionExpired();
        this.setState({
            username: storedUsername || '',
            error: sessionExpired ? '세션이 만료되었습니다. 다시 로그인해주세요.' : '',
        });
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onResize);
    }

    onUsernameChange = (e) => this.setState({ username: e.target.value });
    onPasswordChange = (e) => this.setState({ password: e.target.value });
    onRememberToggle = () => this.setState((prev) => ({ remember: !prev.remember }));
    onShowPasswordToggle = () => this.setState((prev) => ({ showPassword: !prev.showPassword }));

    onSubmit = async (e) => {
        e.preventDefault();
        const { username, password } = this.state;
        if (!username || !password) {
            this.setState({ error: '아이디와 비밀번호를 입력하세요.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        try {
            const response = await loginUser(username, password);
            if (response.status === 200) {
                setLoginSession(username, response.data.access, response.data.refresh);
                window.location.href = '/dashboard';
            } else {
                this.setState({ error: '아이디 또는 비밀번호를 확인해주세요.' });
            }
        } catch (err) {
            console.error('LoginV2 error:', err);
            this.setState({ error: '로그인 중 오류가 발생했습니다.' });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render() {
        const { isMobile } = this.state;
        const handlers = {
            onUsernameChange: this.onUsernameChange,
            onPasswordChange: this.onPasswordChange,
            onRememberToggle: this.onRememberToggle,
            onShowPasswordToggle: this.onShowPasswordToggle,
            onSubmit: this.onSubmit,
        };

        return isMobile
            ? <LoginMobileIsRefresh state={this.state} handlers={handlers} />
            : <LoginDesktopIsRefresh state={this.state} handlers={handlers} />;
    }
}

export default LoginPageV2;
